import {
	CORE_PROJECT,
	SERVICE_DESK_PROJECT,
	SOFTWARE_PROJECT,
	PRODUCT_DISCOVERY_PROJECT,
	type ProjectType,
	CUSTOMER_SERVICE_PROJECT,
} from '@jira/platform__project-types/src/project-types.tsx';
import { PACKAGE_NAME } from '@jira/project-settings__field-types/src/fieldTypesConfig.tsx';
import { logError } from '@jira/project-settings__log-error/src/logError.tsx';

/**
 * When users complete task in SWIFT settings page, a UI flag shows and says "Go back to X"
 * This object configures the URL and _translated_ link text of the UI flag.
 *
 * If this backlink is not configured then SWIFT uses some default text ("Go to project") or similar.
 * Each project type has its own defaults: configured in Product features
 *
 * See https://hello.atlassian.net/wiki/spaces/JPCE/pages/700125807/SWIFT+backlinks
 */
type BackLink = {
	/**
	 * Absolute URL of link going back from settings to wherever user was before
	 * This URL should start with a slash '/' (but SWIFT will prefix a slash if it's not)
	 * This URL must _not_ start with http://
	 * It should not be encoded using encodeURIComponent - the platform code will do that later
	 */
	url: string;
	// Translated string for end users to read
	title: string;
};

const serializeBackLink = (backLink?: BackLink): string => {
	if (backLink == null) {
		return '';
	}
	const { url, title } = backLink;
	return `#backLinkUrl=${encodeURIComponent(url)}&backLinkTitle=${encodeURIComponent(title)}`;
};

// NOTE: consider using getJsmProjectSettingsUrl from src/packages/router-routes/project-settings instead, not to duplicate the logic
export const getNextgenSettingsUrl = (projectType: ProjectType, projectKey: string): string => {
	const products = {
		[CORE_PROJECT]: 'core',
		[SERVICE_DESK_PROJECT]: 'servicedesk',
		[CUSTOMER_SERVICE_PROJECT]: 'servicedesk',
		[SOFTWARE_PROJECT]: 'software',
		[PRODUCT_DISCOVERY_PROJECT]: 'polaris',
	};

	const product = products[projectType];
	if (!product) {
		logError(
			PACKAGE_NAME,
			'urls',
			`Can't build default project settings url for project type: ${projectType}`,
			new Error(`Unexpected project type: ${projectType}`),
		);

		// let's return classic url in that case
		return `/projects/${projectKey}/settings`;
	}

	if (projectType === PRODUCT_DISCOVERY_PROJECT) {
		return `/jira/${product}/projects/${projectKey}/ideas/settings`;
	}

	return `/jira/${product}/projects/${projectKey}/settings`;
};

export const getNextgenSettingsIssueTypesUrl = (
	projectType: ProjectType,
	projectKey: string,
	issueTypeId?: string,
	backLink?: BackLink,
): string => {
	if (projectType === PRODUCT_DISCOVERY_PROJECT) {
		return `${getNextgenSettingsUrl(projectType, projectKey)}`;
	}

	return `${getNextgenSettingsUrl(projectType, projectKey)}/issuetypes${
		issueTypeId ? `/${issueTypeId}` : ''
	}${serializeBackLink(backLink)}`;
};

export const getNextgenSettingsWorkflowUrl = (
	projectType: ProjectType,
	projectKey: string,
	issueTypeId: string,
): string => {
	if (projectType === PRODUCT_DISCOVERY_PROJECT) {
		return `${getNextgenSettingsUrl(projectType, projectKey)}/workflow`;
	}
	return `${getNextgenSettingsUrl(projectType, projectKey)}/issuetypes/${issueTypeId}/workflow`;
};

export const getNextgenSettingsJSDAutomationUrl = (projectKey: string): string =>
	`${getNextgenSettingsUrl(SERVICE_DESK_PROJECT, projectKey)}/automation`;

export const getClassicJSWSettingsUrl = (projectKey: string): string =>
	`/jira/software/c/projects/${projectKey}/settings`;

export const getProjectSettingsDetailsUrl = (
	projectType: ProjectType,
	projectKey: string,
	isSimplified: boolean,
): string => {
	const urlBase =
		projectType === SOFTWARE_PROJECT && !isSimplified
			? getClassicJSWSettingsUrl(projectKey)
			: getNextgenSettingsUrl(projectType, projectKey);

	return `${urlBase}/details`;
};

/**
 * For JSW CMP projects only.
 * Goes directly to "Issue layout" configuration page, a.k.a. SWIFT
 */
export const getClassicIssueLayoutSettingsUrl = ({
	projectKey,
	viewScreenId,
	backLink,
}: {
	projectKey: string;
	viewScreenId: number;
	backLink: BackLink | undefined;
}): string =>
	`/plugins/servlet/project-config/${projectKey}/issuelayout?screenId=${viewScreenId}${serializeBackLink(
		backLink,
	)}`;

/**
 * JSM CMP projects only.
 * Goes to Request type layout configuration page, a.k.a. SWIFT
 */
export const getClassicRequestTypeLayoutSettingsUrl = ({
	projectKey,
	requestTypeId,
	backLink,
}: {
	projectKey: string;
	requestTypeId: string | number;
	backLink: BackLink | undefined;
}): string =>
	`/jira/servicedesk/projects/${projectKey}/settings/request-types/request-type/${requestTypeId}${serializeBackLink(
		backLink,
	)}`;
